import clsx from 'clsx';
import React from 'react';

import type { Block } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../../navigation';
import { EditorSectionIDsContext } from '../contexts';
import { useURLHashSectionId } from './useURLHashSectionId';

/**
 * Hook to add anchor link to block and handle its behaviour.
 * @param {Block} block Anchored block
 * @param {object} options Anchor element options that can determine accessibility label, anchor icon size and onPress handle.
 * @returns {object} Object containing anchor element to be displayed, and the anchored block id, its interactive state and
 * onAnchoredBlockPress function that's used to handle some side effects from click event on the anchored block.
 */
export function useBlockAnchor(
    block: Block,
    options: {
        label: string;
        /** If additional actions are needed on the anchor click */
        onClick?: () => void;
        iconSizeClassName?: string;
    }
): {
    anchorElement: React.ReactElement;
    blockProps: {
        id: string;
        toggleAnchorHash: (isActiveHash: boolean) => void;
        className: string;
    };
} {
    const { navigateToHash } = useNavigation();
    const { sections: sectionIDs } = React.useContext(EditorSectionIDsContext);
    const blockId = sectionIDs.get(block.key);

    // Watch changes of the URL and update a hash state to force re-render of the anchor element.
    const hashSectionId = useURLHashSectionId();

    /**
     * Toggle the section hash in the URL.
     */
    const toggleAnchorHash = React.useCallback(
        (isActiveHash: boolean) => {
            if (!blockId) {
                return;
            }
            if (isActiveHash) {
                navigateToHash('', { scroll: false });
            } else {
                navigateToHash(blockId, { scroll: true });
            }
        },
        [blockId, navigateToHash]
    );
    /**
     * Handle click events on the anchor block.
     */
    const handleBlockAnchorClick = useEventCallback((event) => {
        if (!blockId) {
            return;
        }

        toggleAnchorHash(blockId === hashSectionId);

        if (options.onClick) {
            options.onClick();
        }

        // Needs to be at the end to let scrolling happen.
        event.preventDefault();
    });

    return {
        anchorElement: (
            <a
                href={`#${blockId}`}
                onClick={handleBlockAnchorClick}
                className={clsx(
                    'inline-flex shrink-0 cursor-pointer text-muted hover:text-primary',
                    'group-hover/block-anchor:opacity-100 group-focus/block-anchor:opacity-100',
                    hashSectionId === blockId ? 'opacity-100' : 'opacity-0'
                )}
                style={{ fontSize: 'initial' }}
                aria-label={options.label}
            >
                <Icon.Hash role="presentation" className={options.iconSizeClassName || 'size-4'} />
            </a>
        ),
        blockProps: {
            id: blockId,
            toggleAnchorHash,
            className: 'group/block-anchor',
        },
    };
}
